import type { Grade } from '@backmarket/http-api/src/api-models/Grade'

/**
 * Canonical backbox grade labels used by the reviews service, ordered from
 * best condition to most economical. Matches the ordering exposed in the
 * reviews UI grade filter (`useGradeFilter`).
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/review-service-api/definition#tag/product/operation/get-reviews-products-id-reviews}
 */
export const BACKBOX_GRADE_LABELS = [
  'PREMIUM',
  'FLAWLESS',
  'VERY_GOOD',
  'ECO',
] as const satisfies Grade['name'][]

export type BackboxGradeLabel = (typeof BACKBOX_GRADE_LABELS)[number]

/**
 * Numeric backbox grade values keyed by label. Used to match the grade value
 * from the URL (`useUrlParams().grade`) to the corresponding grade label.
 */
export const GRADE_VALUE_BY_LABEL: Record<BackboxGradeLabel, number> = {
  PREMIUM: 9,
  FLAWLESS: 10,
  VERY_GOOD: 11,
  ECO: 12,
}

export const GRADE_LABEL_BY_VALUE: Record<number, BackboxGradeLabel> =
  Object.fromEntries(
    (
      Object.entries(GRADE_VALUE_BY_LABEL) as Array<[BackboxGradeLabel, number]>
    ).map(([label, value]) => [value, label]),
  )

/**
 * Default fallback image for each grade label, used as the first slide in the
 * carousel when review photos are available.
 */
export const GRADE_DEFAULT_IMAGE: Record<BackboxGradeLabel, string> = {
  PREMIUM: '/img/product/funnel/smartphone/grades/premium.png',
  FLAWLESS: '/img/product/funnel/smartphone/grades/excellent.png',
  VERY_GOOD: '/img/product/funnel/smartphone/grades/good.png',
  ECO: '/img/product/funnel/smartphone/grades/fair.png',
}

/**
 * Minimum global rate a review must have to be eligible for the per-grade
 * aggregation. Keeps low-quality reviews out of the curated picture set.
 */
export const MIN_RATE = 3.0

/**
 * Page size requested when fetching reviews for the per-grade aggregation.
 */
export const PAGE_SIZE = 20
